import Enumerable from "linq";
import { IInternalScriptProcessible } from "..";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import store from "../../../../store";
import { Arguments } from ".";
import { KeyValuePair } from "../../../../domain/system";

export class ValueConverter implements IInternalScriptProcessible {
  public isProcessible(request: ExecuteRequest): boolean {
    const configuration = store.state.configuration.behavior.localhost.internalScript.scripts.valueConverter;
    return configuration.isEnable
        && (new RegExp(configuration.pathExpression, "i")).test(this.toModuleName(request.exePath))
        && this.toKey(request) === "evaluate";
  }

  private toKey(request: ExecuteRequest): string {
    return this.toArguments(request).getValue("-action");
  }

  private toArguments(request: ExecuteRequest): Arguments {
    return new Arguments(request.arguments);
  }

  private toModuleName(path: string): string {
    if (!path) { return path; }
    const splitted = path.split("\\");
    return Enumerable.from(splitted).lastOrDefault();
  }

  public async process(request: ExecuteRequest): Promise<ExternalScriptExecuteResult> {
    const values = this.getValues(request);
    return this.toResult(request.scriptLinkItems, values);
  }

  private getValues(request: ExecuteRequest): Enumerable.IDictionary<string, string> {
    const sourceValue = this.getSourceValue(request);
    if (sourceValue == null) {
      // 値を取得するオブジェクトが見つからないケース
      // フォーマット作成ミスが無い限りはここは通らない
      return null;
    }
    const conditions = this.extractConditions(request.arguments);
    for (const condition of conditions) {
      if (condition.isMatch(sourceValue)) {
        return condition.values;
      }
    }
    return null;
  }

  private toResult(scriptLinkItems: ScriptLinkItem[], values: Enumerable.IDictionary<string, string>): ExternalScriptExecuteResult {
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.name = undefined;
    result.values = [];
    result.compressedCsvText = "";
    result.scriptLinkResults = this.toScriptLinkResults(scriptLinkItems, values);
    return result;
  }

  private getSourceValue(request: ExecuteRequest): string {
    const args = this.toArguments(request);
    const sourceObjectName = args.getValue("-source");
    const found = Enumerable.from(request.scriptLinkItems)
      .where(i => i.name === sourceObjectName)
      .firstOrDefault();
    if (!found) { return null; }
    return (found.value != null)
      ? found.value
      : "";
  }

  private extractConditions(args: string[]): ConditionItem[] {
    if (!args) { return []; }
    let isCondition = false;
    const conditions: ConditionItem[] = [];
    for (const arg of args) {
      if (arg === "-conditions") {
        isCondition = true;
        continue;
      }
      if (isCondition) {
        conditions.push(new ConditionItem(arg));
      }
    }
    return conditions;
  }
  
  toScriptLinkResults(scriptLinkItems: ScriptLinkItem[], values: Enumerable.IDictionary<string, string>): ScriptLinkItem[] {
    if (!values) { return []; }
    return Enumerable.from(scriptLinkItems)
      .where(i => values.contains(i.name))
      .select(i => this.toClonedItem(i, values.get(i.name)))
      .toArray();
  }

  toClonedItem(item: ScriptLinkItem, value: string): ScriptLinkItem {
    const cloned = item.clone();
    cloned.value = value;
    return cloned;
  }
}

class ConditionItem {
  private _param: ConditionParameter;
  private _values: Enumerable.IDictionary<string, string>;

  constructor(text: string) {
    const splitted = text.split(":::");
    this._param = new ConditionParameter(splitted[0]);
    this._values = Enumerable.from(this.toValues(splitted[1]))
      .toDictionary(p => p.key, p => p.value);
  }

  toValues(text: string): KeyValuePair<string, string>[] {
    if (!text) { return []; }
    const values: KeyValuePair<string, string>[] = [];
    for (const pair of text.split(",")) {
      const splitted = pair.split("=");
      const value = new KeyValuePair<string, string>();
      value.key = splitted[0];
      value.value = splitted[1];
      values.push(value);
    }
    return values;
  }

  isMatch(text: string): boolean {
    return this._param.isMatch(text);
  }

  get values(): Enumerable.IDictionary<string, string> {
    return this._values;
  }
}

class ConditionParameter {
  private readonly _isRange: boolean;
  private readonly _lower: number;
  private readonly _upper: number;
  private readonly _texts: string[];
  private readonly _isTerminator: boolean;

  // trueのとき同値は含まない
  private readonly _isLowerOpen: boolean;
  private readonly _isUpperOpen: boolean;
  
  constructor(text: string) {
    // 無条件で一致
    if (!text || text === "ELSE") {
      this._isTerminator = true;
      return;
    }
    // テキスト完全一致
    if (text.startsWith("TEXT")) {
      const trimmed = text.replace(/^TEXT\[/g, "").replace(/\]$/g, "");
      this._texts = trimmed.split(",").map(t => t.trim());
      return;
    }
    // 数値の範囲で判定
    if (text.startsWith("(") || text.startsWith(")") || text.startsWith("[") || text.startsWith("]")) {
      this._isRange = true;
      if (text.startsWith("(") || text.startsWith("]")) {
        this._isLowerOpen = true;
      }
      if (text.endsWith(")") || text.endsWith("[")) {
        this._isUpperOpen = true;
      }
      const splitted = this.removeBrackets(text).split(",").map(t => t.trim());
      this._lower = !splitted[0] ? null : Number(splitted[0]);
      this._upper = !splitted[1] ? null : Number(splitted[1]);
      return;
    }
  }

  private removeBrackets(text: string): string {
    if (!text) { return text; }
    const processingText = (text.startsWith("(") || text.startsWith(")") || text.startsWith("[") || text.startsWith("]"))
      ? text.substring(1, text.length)
      : text;

    return (processingText.endsWith("(") || processingText.endsWith(")") || processingText.endsWith("[") || processingText.endsWith("]"))
      ? processingText.substring(0, processingText.length - 1)
      : processingText;
  }
  
  isMatch(text: string): boolean {
    // 無条件で一致
    if (this._isTerminator) {
      return true;
    }

    const trimmed = !text ? "" : text.trim();

    // テキスト完全一致
    if (this._texts) {
      return this._texts.indexOf(trimmed) >= 0;
    }
    // 数値の範囲で判定
    if (this._isRange) {
      const commaRemoved = trimmed.replace(/,/g, "");
      if (!commaRemoved) { return false; }
      const value = Number(commaRemoved);
      if (!isFinite(value) || isNaN(value)) { return false; }
      if (this._isLowerOpen && this._lower != null && this._lower === value) {
        return false;
      }
      if (this._isUpperOpen && this._upper != null && this._upper === value) {
        return false;
      }
      if (this._lower != null && this._lower > value) {
        return false;
      }
      if (this._upper != null && this._upper < value) {
        return false;
      }
      return true;
    }
  }
}
